import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, CreditCard, Mail, MapPin, Info, Printer, Shield } from 'lucide-react';
import Layout from '../Layout';
import { LoisirManager } from '../../helpers/loisir';
import { showToast } from '../../helpers/customToast';
import { checkSwiklyStatus, createSwiklyDeposit } from '../../helpers/swikly';

const LoisirConfirmationPage = () => {
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [swiklyLink, setSwiklyLink] = useState(null);
  const [swiklyProcessing, setSwiklyProcessing] = useState(false);
  const initializeAttempted = useRef(false);

  useEffect(() => {
    const initializePage = async () => {
      // Skip if initialization was already attempted
      if (initializeAttempted.current) {
        return;
      }
      initializeAttempted.current = true;

      try {
        setLoading(true);
        const { orderId, documentId, paymentConfirmed, swiklyLink: initialSwiklyLink } = location.state || {};

        if (!orderId || !documentId) {
          showToast('Commande introuvable', 'error');
          return;
        }

        // First set swikly link if it was passed in location state
        if (initialSwiklyLink) {
          setSwiklyLink(initialSwiklyLink);
        }

        const orderData = await LoisirManager.getFormulaOrderByDocumentId(documentId);
        if (!orderData) {
          showToast('Impossible de charger les détails de la commande', 'error');
          return;
        }

        setOrder(orderData);

        // If payment is confirmed, initiate Swikly deposit request if not already done
        if (paymentConfirmed && orderData.deposit_amount > 0 && !initialSwiklyLink) {
          try {
            setSwiklyProcessing(true);

            // If we already have a Swikly request ID, get the link from existing request
            if (orderData.swikly_request_id) {
              const swiklyStatus = await checkSwiklyStatus(orderData.swikly_request_id);
              setSwiklyLink(swiklyStatus.link);
            } else {
              // Otherwise create new deposit request
              const swiklyResponse = await createSwiklyDeposit({
                orderId: orderData.id,
                depositAmount: orderData.deposit_amount,
                customerEmail: orderData.userDetails.email,
                firstName: orderData.userDetails.first_name,
                lastName: orderData.userDetails.last_name,
                startDate: orderData.start_date,
                endDate: orderData.end_date
              });
              setSwiklyLink(swiklyResponse.link);
            }
          } catch (error) {
            console.error('Error creating Swikly deposit:', error);
            showToast('Erreur lors de la création du dépôt de garantie', 'error');
          } finally {
            setSwiklyProcessing(false);
          }
        }
      } catch (error) {
        console.error('Error initializing confirmation page:', error);
        showToast('Une erreur est survenue', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [location]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const handlePrint = () => {
    window.print();
  };

  const getTotalSelectedItems = () => {
    if (!order?.selected_products_details) return order?.selected_products?.length || 0;
    
    return Object.values(order.selected_products_details).reduce((sum, details) => {
      return sum + (details.quantity || 1);
    }, 0);
  };

  const getProductQuantity = (productId) => {
    if (!order?.selected_products_details) return 1;
    return order.selected_products_details[productId]?.quantity || 1;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-danger" role="alert">
            Commande introuvable
          </div>
          <Link to="/" className="btn btn-primary">
            Retour à l'accueil
          </Link>
        </div>
      </Layout>
    );
  }

  const renderPaymentStatus = () => {
    if (location.state?.paymentConfirmed) {
      return <span className="badge bg-success">Payée</span>;
    }
    return <span className="badge bg-warning">En attente de paiement</span>;
  };

  const renderDepositSection = () => {
    if (!order.deposit_amount) return null;

    return (
      <div className="card mb-4">
        <div className="card-body">
          <div className="d-flex align-items-center mb-3">
            <Shield className="text-primary me-2" size={24} />
            <h3 className="card-title mb-0">Dépôt de Garantie</h3>
          </div>

          {swiklyProcessing ? (
            <div className="text-center py-3">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Création du dépôt en cours...</span>
              </div>
              <p className="mt-2">Création du dépôt de garantie en cours...</p>
            </div>
          ) : swiklyLink ? (
            <>
              <div className="alert alert-info mb-3">
                <Info size={18} className="me-2" />
                Pour finaliser votre réservation, vous devez maintenant déposer une garantie de {order.deposit_amount}€ via Swikly.
              </div>
              <div className="text-center">
                <a
                  href={swiklyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-lg"
                >
                  <Shield size={18} className="me-2" />
                  Effectuer le dépôt de garantie
                </a>
                <p className="text-muted mt-2">
                  <small>Le lien a également été envoyé à votre adresse email.</small>
                </p>
              </div>
            </>
          ) : (
            <div className="alert alert-info">
              <Info size={18} className="me-2" />
              Un dépôt de garantie de {order.deposit_amount}€ sera requis.
              Vous recevrez un email de Swikly pour effectuer ce dépôt.
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Layout className="main-layout">
      <div className="container my-5 print-content">
        <div className="row mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h2>Confirmation de commande Loisirs</h2>
            <button
              onClick={handlePrint}
              className="btn btn-outline-secondary d-print-none"
            >
              <Printer size={18} className="me-2" />
              Imprimer
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-4">
            <div className="alert alert-success" role="alert">
              <h4 className="alert-heading">Merci pour votre commande !</h4>
              <p>Votre commande a été enregistrée avec succès. Un email de confirmation vous a été envoyé.</p>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Détails de la commande</h3>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Type:</strong>
                  </div>
                  <div className="col-sm-8">
                    {order.is_subscription ? (
                      <span className="badge bg-success">Abonnement mensuel</span>
                    ) : (
                      <span>Location temporaire</span>
                    )}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Numéro de commande:</strong>
                  </div>
                  <div className="col-sm-8">
                    #{order.id}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Date de commande:</strong>
                  </div>
                  <div className="col-sm-8">
                    {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Statut:</strong>
                  </div>
                  <div className="col-sm-8">
                    {renderPaymentStatus()}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Formule:</strong>
                  </div>
                  <div className="col-sm-8">
                    {order.formula?.name} - {order.duration_days} jours
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Période:</strong>
                  </div>
                  <div className="col-sm-8">
                    {formatDate(order.start_date)} - {formatDate(order.end_date)}
                  </div>
                </div>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Coordonnées</h3>
                <p>
                  {order.userDetails ? (
                    <>
                      <strong>{order.userDetails.first_name} {order.userDetails.last_name}</strong><br />
                      {order.userDetails.address_1}<br />
                      {order.userDetails.address_2 ? `${order.userDetails.address_2}<br />` : ''}
                      {order.userDetails.postal_code} {order.userDetails.city}<br />
                      {order.userDetails.country}<br />
                      &nbsp;<br />
                      Email: {order.userDetails.email}<br />
                      Tél : {order.userDetails.phone}
                    </>
                    ) : (
                    <>
                      <strong>{order.user_details.first_name} {order.user_details.last_name}</strong><br />
                      {order.user_details.address_1}<br />
                      {order.user_details.address_2 ? `${order.user_details.address_2}<br />` : ''}
                      {order.user_details.postal_code} {order.user_details.city}<br />
                      {order.user_details.country}<br />
                      &nbsp;<br />
                      Email: {order.user_details.email}<br />
                      Tél : {order.user_details.phone}
                    </>
                  )}
                </p>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h3 className="card-title mb-0">Articles sélectionnés</h3>
                  <span className="badge bg-primary">{getTotalSelectedItems()} articles</span>
                </div>
                {order.selected_products && order.selected_products.map((product, index) => {
                  const quantity = getProductQuantity(product.id);
                  return (
                    <div key={index} className="card mb-3">
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-8">
                            <h5 className="card-title">
                              {product.title}
                              {quantity > 1 && (
                                <span className="badge bg-secondary ms-2">Quantité: {quantity}</span>
                              )}
                            </h5>
                          </div>
                          <div className="col-md-4 text-end">
                            {quantity > 1 && (
                              <small className="text-muted">x{quantity}</small>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {order?.comments && (
              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Informations supplémentaires</h3>
                  <div className="card">
                    <div className="card-body">
                      <p>{order.comments}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Résumé</h3>
                <div className="d-flex justify-content-between mb-3">
                  <span>Formule</span>
                  <strong>{(order.total_price - order.delivery_price).toFixed(2)}€</strong>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Livraison <small>({order.delivery_method === 'home_delivery' ? 'À domicile' : 'En point relais'})</small></span>
                  <strong>{order.delivery_price.toFixed(2)}€</strong>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>Total</span>
                  <strong>{order.total_price.toFixed(2)}€</strong>
                </div>
              </div>
            </div>

            {order.is_subscription && (
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex align-items-center mb-3">
                    <Calendar className="text-primary me-2" size={24} />
                    <h3 className="card-title mb-0">Informations sur l'abonnement</h3>
                  </div>
                  <div className="alert alert-info">
                    <p className="mb-2"><strong>Votre abonnement en un coup d'œil :</strong></p>
                    <ul className="mb-0">
                      <li>Facturation automatique de {order.total_price.toFixed(2)}€ chaque mois</li>
                      <li>Vous recevrez une facture par email après chaque prélèvement</li>
                      <li>Vous pouvez annuler à tout moment depuis votre espace client</li>
                      <li>En cas de question, n'hésitez pas à contacter notre service client</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Deposit section */}
            {renderDepositSection()}

            <div className="card mb-4 d-print-none">
              <div className="card-body">
                <h3 className="card-title">Prochaines étapes</h3>
                <ol className="list-unstyled">
                  {order.is_subscription && (
                    <li className="mb-3">
                      <div className="d-flex">
                        <Calendar size={24} className="me-3 text-primary" />
                        <div>
                          <strong>Renouvellement automatique</strong><br />
                          Votre abonnement sera automatiquement renouvelé chaque mois
                        </div>
                      </div>
                    </li>
                  )}
                  {!location.state?.paymentConfirmed && (
                    <li className="mb-3">
                      <div className="d-flex">
                        <CreditCard size={24} className="me-3 text-primary" />
                        <div>
                          <strong>Paiement</strong><br />
                          Vous allez recevoir un email pour procéder au paiement
                        </div>
                      </div>
                    </li>
                  )}
                  {order.deposit_amount > 0 && (
                    <li className="mb-3">
                      <div className="d-flex">
                        <Shield size={24} className="me-3 text-primary" />
                        <div>
                          <strong>Dépôt de garantie</strong><br />
                          {swiklyLink ? 'Cliquez sur le bouton ci-dessus pour effectuer votre dépôt' : 'Un email vous sera envoyé pour le dépôt de garantie'}
                        </div>
                      </div>
                    </li>
                  )}
                  <li className="mb-3">
                    <div className="d-flex">
                      <Mail size={24} className="me-3 text-primary" />
                      <div>
                        <strong>Confirmation</strong><br />
                        Un email de confirmation vous sera envoyé après le paiement
                      </div>
                    </div>
                  </li>
                  <li className="mb-3">
                    <div className="d-flex">
                      <MapPin size={24} className="me-3 text-primary" />
                      <div>
                        <strong>Retrait</strong><br />
                        Instructions de retrait dans l'email de confirmation
                      </div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 d-print-none">
          <Link to="/" className="btn btn-primary">
            Retour à l'accueil
          </Link>
        </div>
      </div>

      <style>{`
        @media print {
          /* Hide everything except our print content */
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Hide header and footer */
          header, footer, nav {
            display: none !important;
          }
          
          /* Other print styles */
          .container {
            width: 100%;
            max-width: none;
          }
          
          .card {
            border: none !important;
            break-inside: avoid;
          }
          
          .card-body {
            padding: 0;
          }
          
          .btn, .d-print-none {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </Layout>
  );
};

export default LoisirConfirmationPage;